import React, { useState, useRef } from "react";

import {
  Text,
  Flex,
  Label,
  Input,
  SxProp,
  Button,
  ThemeUICSSProperties,
  CSSPseudoSelectorProps,
  ThemeUIStyleObject,
  Divider,
  Box,
  Spinner
} from "theme-ui";

import { BaseProp } from "../../@types/base-types";
import { useIsMobile } from "../../hooks/useIsMobile";
import { getAmountToDisplay } from "../../utils/digits";
import { Tooltip } from "../Tooltip";

type RowProps = SxProp & {
  label: string;
  labelId?: string;
  labelFor?: string;
  labelVariant?: "row" | "column";
  infoIcon?: React.ReactNode;
  layout?: "vertical" | "horizontal";
} & BaseProp;

const columnLayout: ThemeUIStyleObject = {
  flexDirection: "column",
  height: "100%",
  width: "100%"
};

const rowLayout: ThemeUIStyleObject = {
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "center",
  height: "100%",
  width: "100%"
};

export const Row: React.FC<RowProps> = ({
  sx,
  label,
  labelId,
  labelFor,
  labelVariant,
  children,
  infoIcon
}) => {
  return labelVariant === "row" ? (
    <Flex sx={{ ...rowLayout, gap: "12px", ...sx }}>
      {label && (
        <Label
          id={labelId}
          htmlFor={labelFor}
          sx={{
            borderColor: "transparent"
          }}
        >
          <Flex sx={{ alignItems: "center" }}>
            <Text color="success" variant="heading">
              {label}
            </Text>
            {infoIcon && infoIcon}
          </Flex>
        </Label>
      )}
      {children}
    </Flex>
  ) : (
    <Flex sx={{ ...columnLayout, ...sx }}>
      {label && (
        <Label
          id={labelId}
          htmlFor={labelFor}
          sx={{
            borderColor: "transparent"
          }}
        >
          <Flex sx={{ alignItems: "center" }}>
            {label}
            {infoIcon && infoIcon}
          </Flex>
        </Label>
      )}
      {children}
    </Flex>
  );
};

type StaticAmountsProps = {
  amount: string;
  amountSx?: ThemeUIStyleObject;
  amountValue?: React.ReactNode;
  amountValueSx?: ThemeUIStyleObject;
  bgColor?: string;
  color?: string;
  inputId: string;
  labelledBy?: string;
  onClick?: () => void;
  pendingAmount?: string;
  pendingColor?: string;
  shortenAmountThreshold?: number;
  subUnit?: string;
  unit?: string;
  unitSx?: ThemeUIStyleObject;
  unitFontSize?: number | string | number[] | string[];
  unitIcon?: React.ReactNode;
  input?: React.ReactNode;
  leftIcon?: boolean;
  valueRowSx?: ThemeUIStyleObject;
  amountLoading?: boolean;
} & BaseProp;

export const StaticAmounts: React.FC<StaticAmountsProps & SxProp> = ({
  amount,
  amountSx,
  amountValue,
  amountValueSx,
  bgColor,
  children,
  color,
  inputId,
  labelledBy,
  onClick,
  subUnit,
  sx,
  shortenAmountThreshold,
  unit,
  unitSx,
  unitFontSize,
  unitIcon,
  input,
  leftIcon = false,
  valueRowSx,
  amountLoading
}) => {
  const [amountToDisplay, shouldShowTooltip] = getAmountToDisplay(
    amount,
    shortenAmountThreshold ?? 12
  );

  const isMobile = useIsMobile();

  const amounts = () => {
    const content = (
      <Text variant="text.mediumH1" sx={{ color: "B100", ...amountSx }}>
        {shouldShowTooltip ? `${amountToDisplay} ` : `${amount} `}
      </Text>
    );
    return input ? (
      input
    ) : shouldShowTooltip ? (
      <Tooltip message={amount} placement={"top"}>
        {content}
      </Tooltip>
    ) : (
      content
    );
  };

  const subUnitRender = () => {
    return (
      <>
        {subUnit && (
          <Text
            variant="text.mediumH3"
            sx={{
              color: "B60"
            }}
          >
            {subUnit}
          </Text>
        )}
      </>
    );
  };

  const amountsWithUnit = () => {
    const content = (
      <Text sx={{ color, fontWeight: "heading", lineHeight: "24px", ...amountSx }}>
        {shouldShowTooltip ? `${amountToDisplay} ` : `${amount} `}
        <Text
          sx={{
            fontWeight: "body",
            minWidth: "auto",
            fontSize: unitFontSize || [1, 2, 3]
          }}
        >
          {unit}
        </Text>
      </Text>
    );
    return input ? (
      input
    ) : shouldShowTooltip ? (
      <Tooltip message={amount} placement={"top"}>
        {content}
      </Tooltip>
    ) : (
      content
    );
  };

  return (
    <Flex
      id={inputId}
      aria-labelledby={labelledBy}
      {...{ onClick }}
      sx={{
        alignItems: "center",
        ...(onClick ? { cursor: "text" } : {}),
        ...staticStyle,
        ...sx
      }}
    >
      {unitIcon && leftIcon ? (
        <Flex
          sx={{
            flexDirection: "row",
            alignItems: "flex-start",
            width: "100%",
            gap: "12px",
            ...valueRowSx
          }}
        >
          <Flex
            sx={{ alignItems: "center", justifyContent: "center", height: "36px", width: "36px" }}
          >
            {unitIcon}
          </Flex>

          <Flex
            sx={{
              flexDirection: "column",
              justifyContent: "center"
            }}
          >
            {amountLoading ? (
              <Spinner />
            ) : (
              <>
                {amounts()}

                {amountValue && (
                  <Text
                    variant="text.regularH3"
                    sx={{
                      color: "B40",
                      mt: 1,
                      ...amountValueSx
                    }}
                  >
                    ≈ {amountValue}
                  </Text>
                )}
              </>
            )}
          </Flex>

          {children}

          {unit && (
            <Box sx={{ width: "auto", marginTop: 3, ...unitSx }}>
              <Text
                variant="text.mediumH3"
                sx={{
                  color: "B100"
                }}
              >
                {unit}
              </Text>
            </Box>
          )}
        </Flex>
      ) : unitIcon && !leftIcon ? (
        <Flex sx={{ flexDirection: "column", width: "100%" }}>
          <Flex
            sx={{
              flexDirection: "row",
              justifyContent: "center",
              alignItems: "center",
              width: "100%"
            }}
          >
            <Flex
              sx={{
                flexDirection: "column",
                flex: 1
              }}
            >
              {amounts()}

              {amountValue && (
                <Text
                  variant="text.regularH3"
                  sx={{
                    color: "B40",
                    mt: 1
                  }}
                >
                  ≈ {amountValue}
                </Text>
              )}
            </Flex>

            {children}

            {isMobile ? null : (
              <>
                {subUnitRender()}

                <Divider sx={{ height: "28px", width: "1px", bg: "G40", mx: 6 }} />

                {unitIcon}

                {unit && (
                  <Text
                    variant="text.mediumH3"
                    sx={{
                      color: "B100",
                      ml: 2
                    }}
                  >
                    {unit}
                  </Text>
                )}
              </>
            )}
          </Flex>
          {isMobile && <Divider sx={{ height: "1px", width: "100%", bg: "G40" }} />}

          {isMobile && (
            <Flex sx={{ flexDirection: "row", alignItems: "center", gap: "12px", mt: 2 }}>
              {unitIcon}

              {unit && (
                <Text
                  variant="text.mediumH3"
                  sx={{
                    color: "B100",
                    ml: 2
                  }}
                >
                  {unit}
                </Text>
              )}
            </Flex>
          )}
        </Flex>
      ) : (
        // read and no unit icon
        <Flex
          sx={{
            flexDirection: "row",
            alignItems: "center",
            justifyContent: "flex-end",
            width: "100%"
          }}
        >
          <Flex sx={{ flexDirection: "row", alignItems: "center", gap: "12px" }}>
            <Flex
              sx={{
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "flex-end",
                p: 0,
                height: "44px"
              }}
            >
              {amountsWithUnit()}

              {amountValue && (
                <Text
                  sx={{
                    color: "text-65",
                    fontSize: 0,
                    fontWeight: "body"
                  }}
                >
                  ≈ {amountValue}
                </Text>
              )}
            </Flex>

            {children}
          </Flex>
        </Flex>
      )}
    </Flex>
  );
};

const staticStyle: ThemeUICSSProperties = {
  flexGrow: 1,
  height: "100%",
  width: "100%"
};

const editableLayout: ThemeUICSSProperties = {
  flexGrow: 1,
  p: "12px 24px",
  height: "100%",
  width: "100%"
};

const editableStyle: ThemeUICSSProperties & CSSPseudoSelectorProps = {
  ...editableLayout,
  fontSize: [4, 5],
  border: 1,
  borderRadius: "20px",
  borderColor: "G40",
  bg: "LG40",

  ":focus": {
    borderColor: "primary",
    outline: "none"
  }
};

type StaticRowProps = RowProps &
  StaticAmountsProps & {
    unitFontSize?: number | string;
    amountSx?: ThemeUIStyleObject;
  };

export const StaticRow: React.FC<StaticRowProps> = ({
  label,
  labelId,
  labelFor,
  infoIcon,
  sx,
  amountSx,
  unitFontSize,
  ...props
}) => {
  const properties = {
    ...props,
    unitFontSize,
    amountSx,
    sx: {
      ...sx
    }
  };
  return (
    <Row {...{ label, labelId, labelFor, infoIcon }}>
      <StaticAmounts {...properties} />
    </Row>
  );
};

type DisabledEditableRowProps = Omit<StaticAmountsProps, "labelledBy" | "onClick"> & {
  label: string;
  bgColor?: string;
};

export const DisabledEditableRow: React.FC<DisabledEditableRowProps> = ({
  inputId,
  label,
  unit,
  amount,
  color,
  bgColor = "rgba(255, 255, 255, 0.13)",
  pendingAmount,
  pendingColor
}) => (
  <Row labelId={`${inputId}-label`} {...{ label, unit }}>
    <StaticAmounts
      sx={{ ...editableStyle, borderColor: "transparent" }}
      labelledBy={`${inputId}-label`}
      {...{ inputId, amount, bgColor, unit, color, pendingAmount, pendingColor }}
    />
  </Row>
);

type EditableRowProps = DisabledEditableRowProps & {
  editingState: [string | undefined, (editing: string | undefined) => void];
  editedAmount: string;
  setEditedAmount: (editedAmount: string) => void;
  maxAmount?: string;
  maxedOut?: boolean;
  min?: string;
  max?: string;
  pattern?: string;
  labelVariant?: "row" | "column"; // Variant for adding something new like Deposit Borrow or Stake labels
  // a state to indicate if the input is disabled
  disabled?: boolean;
  containerSx?: ThemeUIStyleObject;
};

export const EditableRow: React.FC<EditableRowProps> = ({
  label,
  labelVariant,
  inputId,
  unit,
  unitIcon,
  unitFontSize,
  subUnit,
  amount,
  amountValue,
  color,
  pendingAmount,
  pendingColor,
  editingState,
  editedAmount,
  setEditedAmount,
  maxAmount,
  maxedOut,
  min,
  pattern,
  disabled = false,
  containerSx
}) => {
  const [editing, setEditing] = editingState;
  const [innerEditorValue, updateInnerEditorValue] = useState<string | undefined>();
  const editorRef = useRef<HTMLInputElement | null>(null);
  const [isInvalid, setIsInvalid] = useState(false);

  const invalidColor = "R100";
  // const invalidColor = isInvalid ? "R100" : "transparent";

  /**
   * Update Inner and Outer Editor Value
   * For a better user experience and compatible for existing code
   * @param value input value {string}
   */
  const updateEditorValue = (value: string) => {
    updateInnerEditorValue(value);
    try {
      setEditedAmount(value || "0");
      setIsInvalid(false);
    } catch {
      setIsInvalid(true);
    }
  };

  return (
    <Row labelId={`${inputId}-label`} {...{ label, labelVariant, unit }}>
      <StaticAmounts
        sx={{
          ...editableStyle,
          ...containerSx,
          borderColor: maxedOut || isInvalid ? invalidColor : editableStyle.borderColor,
          ":focus": {
            borderColor: invalidColor,
            color: invalidColor
          }
        }}
        labelledBy={`${inputId}-label`}
        onClick={() => setEditing(inputId)}
        {...{
          inputId,
          amount,
          amountValue,
          unit,
          unitIcon,
          subUnit,
          unitFontSize,
          color,
          pendingAmount,
          pendingColor,
          invalid: isInvalid
        }}
        input={
          disabled ? null : editing === inputId ? (
            <Input
              size={1}
              autoFocus
              ref={editorRef}
              id={inputId}
              type="number"
              min={min}
              pattern={pattern}
              value={parseFloat(editedAmount) === 0 ? undefined : parseFloat(editedAmount)}
              onChange={e => {
                updateEditorValue(e.target.value);
              }}
              onBlur={() => {
                setEditing(undefined);
                setIsInvalid(false);
              }}
              placeholder="0.0000"
              sx={{
                border: 0,
                fontFamily: "Rubik",
                boxShadow: "none",
                fontSize: "22px",
                fontWeight: "500",
                lineHeight: "36px",
                maxHeight: "36px",
                appearance: "none",
                padding: "0px 2px px 0px",
                width: "100%",
                color: "B100",
                bg: "transparent",
                "::-webkit-inner-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                },
                "::-webkit-outer-spin-button": {
                  WebkitAppearance: "none",
                  margin: 0
                }
              }}
            />
          ) : null
        }
      >
        {maxAmount && (
          <Text
            variant="text.mediumH3"
            sx={{
              color: "B60",
              cursor: "pointer"
            }}
            onClick={event => {
              if (maxedOut) return;
              updateEditorValue(maxAmount);
              editorRef.current?.focus();
              event.stopPropagation();
            }}
          >
            Max
          </Text>
        )}
      </StaticAmounts>
    </Row>
  );
};

type EditableRadioRowProps = DisabledEditableRowProps & {
  options: {
    display: string;
    value: number;
    valueToCompare: string;
  }[];
  name: string;
  editingState: [string | undefined, (editing: string | undefined) => void];
  editedAmount: string;
  setEditedAmount: (editedAmount: string) => void;
  maxAmount?: string;
  maxedOut?: boolean;
};

export const EditableRadioRow: React.FC<EditableRadioRowProps> = ({
  options,
  name,
  label,
  inputId,
  unit,
  amount,
  setEditedAmount
}) => {
  return (
    <Row {...{ label, labelFor: inputId, unit }}>
      <Flex
        sx={{
          ...editableLayout,
          pl: 0,
          mb: 0
        }}
      >
        {options.map(({ display, value, valueToCompare }) => {
          return (
            <Label
              key={`radio-${display}`}
              sx={{
                display: "flex",
                p: 0,
                pt: 1
              }}
            >
              <Input
                variant="radio"
                type="radio"
                name={name}
                value={value}
                checked={valueToCompare === amount}
                onChange={e => {
                  setEditedAmount(e.target.value);
                }}
              />
              <Text variant="radio">{display}</Text>
            </Label>
          );
        })}
      </Flex>
    </Row>
  );
};
